import {
  Stack,
  Title,
  Card,
  Text,
  TextInput,
  Select,
  Group,
  Button,
  NumberInput,
  Textarea,
  Stepper,
  Divider,
  Alert,
  ActionIcon,
  Modal,
  Grid,
  Badge,
  Loader,
  Center,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconArrowRight,
  IconArrowBigUp,
  IconArrowBigDown,
  IconBuilding,
  IconCalendar,
  IconCheck,
  IconCircleCheck,
  IconPencil,
  IconPlus,
  IconRoute,
  IconTruck,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import {
  useConductores,
  useCrearDeCA,
  useCreateConductor,
  useCreateEmpresa,
  useCreateOrigenDestino,
  useCreateVehiculo,
  useDeCA,
  useEmpresas,
  useModificarDeCA,
  useOrigenesDestinos,
  useVehiculos,
} from '../../api/hooks';
import { getErrorMessage } from '../../api/client';
import type { DeCaEnriched, EnvioTipoServicio, UnidadCantidad } from '../../api/types';
import { UNIDAD_CANTIDAD_LABEL, UNIDAD_CANTIDAD_MEANING } from '../../api/types';

/**
 * Wizard desktop-friendly para crear O modificar un DeCA.
 *
 * Replica el flujo del movil (Sprint 4 / 7) adaptado a escritorio:
 *  1. Partes: cargador contractual + transportista efectivo + conductor + vehiculo.
 *  2. Trayecto: origen + destino (catalogos OrigenDestino del tenant).
 *  3. Mercancia: naturaleza + cantidad (con unidad: kg/t/m³/L/palets)
 *     + bultos + fechas + observaciones.
 *  4. Confirmar: resumen + motivo (obligatorio en modo edicion) + submit.
 *
 * Modos:
 *   - Sin `editingDeCA`: CREACION atomica (POST /deca). El envio queda en
 *     EMITIDO y el DeCA se emite contra el.
 *   - Con `editingDeCA`: MODIFICACION (POST /deca/:id/modificar). Solo se
 *     envian los campos que difieren del original (PATCH-like). El campo
 *     "motivo" es obligatorio por Apartado Quinto de la Resolucion 5 jun 2026.
 *
 * Catalogos: cada Select tiene un boton "+" que abre un modal in-place
 * con el formulario del catalogo correspondiente (mismo patron que el
 * backoffice ya usa para empresas/vehiculos/conductores/origenes-destinos).
 *
 * Tipo de servicio: fijo en MERCANCIA por ahora. El modelo soporta los
 * 4 tipos legales, pero la UI la creamos cuando los necesitemos (no es
 * MVP).
 */

interface DeCAWizardProps {
  /** Si viene, el wizard se comporta como editor de ese DeCA. */
  editingDeCA?: DeCaEnriched;
}

interface FormState {
  tipoServicio: EnvioTipoServicio;
  cargadorId: string;
  transportistaId: string;
  vehiculoId: string;
  conductorId: string;
  origenId: string;
  destinoId: string;
  fechaCarga: Date | null;
  fechaDescarga: Date | null;
  naturaleza: string;
  // Cantidad transportada + unidad de medida (Sprint X.Y). Antes
  // era `pesoKg` hard-coded en kg; ahora admite kg, t, m³, L o
  // palets. Default KG para mantener la memoria muscular del
  // caso común.
  cantidad: number | '';
  unidadCantidad: UnidadCantidad;
  bultos: number | '';
  observaciones: string;
  motivo: string;
}

const EMPTY: FormState = {
  tipoServicio: 'MERCANCIA',
  cargadorId: '',
  transportistaId: '',
  vehiculoId: '',
  conductorId: '',
  origenId: '',
  destinoId: '',
  fechaCarga: null,
  fechaDescarga: null,
  naturaleza: '',
  cantidad: '',
  unidadCantidad: 'KG' as UnidadCantidad,
  bultos: '',
  observaciones: '',
  motivo: '',
};

// ────────────────────────────────────────────────────────────────────
// Catalogos: alta in-place desde el wizard
// ────────────────────────────────────────────────────────────────────

type ModalCatalogo =
  | { tipo: 'crearEmpresa'; subtipo: 'CARGADOR' | 'TRANSPORTISTA' }
  | { tipo: 'crearVehiculo' }
  | { tipo: 'crearConductor' }
  | { tipo: 'crearOrigenDestino' }
  | null;

interface EmpresaDraft {
  razonSocial: string;
  nifCif: string;
  domicilioFiscal: string;
  municipio: string;
  codigoPostal: string;
  provincia: string;
  telefono: string;
  email: string;
}
interface VehiculoDraft {
  matricula: string;
  matriculaRemolque: string;
  marca: string;
  modelo: string;
  tara: number | '';
  mma: number | '';
}
interface ConductorDraft {
  nombre: string;
  dni: string;
  telefono: string;
  email: string;
  direccion: string;
}
interface OrigenDestinoDraft {
  nombre: string;
  direccion: string;
  codigoPostal: string;
  poblacion: string;
  provincia: string;
  nifCif: string;
}

const STEP_LABELS = ['Partes', 'Trayecto', 'Mercancía', 'Confirmar'] as const;

export function DeCAWizard({ editingDeCA }: DeCAWizardProps = {}) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [state, setState] = useState<FormState>(EMPTY);
  const [modalCatalogo, setModalCatalogo] = useState<ModalCatalogo>(null);

  const isEditing = !!editingDeCA;
  const crear = useCrearDeCA();
  const modificar = useModificarDeCA();

  // Datos de catalogos
  const { data: empresas = [] } = useEmpresas();
  const { data: vehiculos = [] } = useVehiculos();
  const { data: conductores = [] } = useConductores();
  const { data: origenesDestinos = [] } = useOrigenesDestinos();

  const cargadores = useMemo(
    () => empresas.filter((e) => e.tipo === 'CARGADOR' || e.tipo === 'AMBOS'),
    [empresas],
  );
  const transportistas = useMemo(
    () => empresas.filter((e) => e.tipo === 'TRANSPORTISTA' || e.tipo === 'AMBOS'),
    [empresas],
  );

  // Pre-relleno cuando entramos en modo edicion (mismo patron que en
  // `NuevoDeCA.tsx` del movil: una sola vez por DeCA).
  const lastEditingIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!editingDeCA) return;
    if (lastEditingIdRef.current === editingDeCA.id) return;
    lastEditingIdRef.current = editingDeCA.id;
    const envio = editingDeCA.envio;
    // El backend ahora expone `servicios[]` hidratado, no `envio.detalleMercancia`.
    const servicio = (editingDeCA as unknown as { servicios?: Array<Record<string, unknown>> })
      .servicios?.[0];
    setState({
      tipoServicio: (envio?.tipoServicio as EnvioTipoServicio) ?? 'MERCANCIA',
      cargadorId: editingDeCA.cargador?.id ?? '',
      transportistaId: editingDeCA.transportista?.id ?? '',
      vehiculoId: editingDeCA.vehiculo?.id ?? '',
      conductorId: editingDeCA.conductor?.id ?? '',
      origenId:
        (servicio?.origenId as string | undefined) ??
        (servicio?.origen as { id?: string } | undefined)?.id ??
        '',
      destinoId:
        (servicio?.destinoId as string | undefined) ??
        (servicio?.destino as { id?: string } | undefined)?.id ??
        '',
      fechaCarga: envio?.fechaServicio ? new Date(envio.fechaServicio) : null,
      fechaDescarga: envio?.fechaFinServicio ? new Date(envio.fechaFinServicio) : null,
      naturaleza: (servicio?.naturaleza as string | undefined) ?? '',
      cantidad: (typeof servicio?.cantidad === 'number' ? (servicio.cantidad as number) : '') as
        number | '',
      unidadCantidad: ((servicio?.unidadCantidad as UnidadCantidad | undefined) ??
        'KG') as UnidadCantidad,
      bultos: (typeof servicio?.bultos === 'number' ? (servicio.bultos as number) : '') as
        number | '',
      observaciones: (servicio?.observaciones as string | undefined) ?? '',
      motivo: '',
    });
  }, [editingDeCA]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setState((s) => ({ ...s, [key]: value }));

  // ──────────────────────────────────────────────────────────────
  // Helpers de catalogos
  // ──────────────────────────────────────────────────────────────

  const labelFor = <T extends { id: string }>(
    list: T[],
    id: string,
    formatter: (e: T) => string,
  ) => {
    const found = list.find((x) => x.id === id);
    return found ? formatter(found) : '';
  };

  // ──────────────────────────────────────────────────────────────
  // Validaciones por paso (canNextFromN)
  // ──────────────────────────────────────────────────────────────

  const canNextFrom0 =
    !!state.cargadorId && !!state.conductorId && !!state.vehiculoId && !!state.transportistaId;
  const canNextFrom1 = !!state.origenId && !!state.destinoId && state.origenId !== state.destinoId;
  const canNextFrom2 =
    !!state.naturaleza.trim() && state.cantidad !== '' && state.bultos !== '' && !!state.fechaCarga;
  const canSubmit = !isEditing || !!state.motivo.trim();

  // ──────────────────────────────────────────────────────────────
  // Submit
  // ──────────────────────────────────────────────────────────────

  const submittingRef = useRef(false);
  const handleSubmit = async () => {
    if (submittingRef.current) return;
    if (!state.fechaCarga) return;
    if (!canNextFrom0 || !canNextFrom1 || !canNextFrom2) return;
    if (isEditing && !state.motivo.trim()) return;
    submittingRef.current = true;
    try {
      if (isEditing && editingDeCA) {
        // PATCH-like: solo enviamos los campos que difieren del original.
        const envioActual = editingDeCA.envio;
        const servicioActual = (
          editingDeCA as unknown as {
            servicios?: Array<Record<string, unknown>>;
          }
        ).servicios?.[0];
        const envioPatch: {
          vehiculoId?: string;
          conductorId?: string;
          fechaServicio?: string;
          fechaFinServicio?: string;
        } = {};
        if (state.vehiculoId && state.vehiculoId !== editingDeCA.vehiculo?.id) {
          envioPatch.vehiculoId = state.vehiculoId;
        }
        if (state.conductorId && state.conductorId !== editingDeCA.conductor?.id) {
          envioPatch.conductorId = state.conductorId;
        }
        const fsISO = state.fechaCarga.toISOString().slice(0, 10);
        const fsActual = envioActual?.fechaServicio ?? editingDeCA.fechaServicio;
        if (fsISO !== fsActual) envioPatch.fechaServicio = fsISO;
        const ffISO = state.fechaDescarga
          ? state.fechaDescarga.toISOString().slice(0, 10)
          : undefined;
        const ffActual = envioActual?.fechaFinServicio ?? editingDeCA.fechaFinServicio ?? null;
        if (ffISO !== ffActual) envioPatch.fechaFinServicio = ffISO;
        const cambios: Record<string, unknown> = {};
        const origenIdActual =
          (servicioActual?.origenId as string | undefined) ??
          (servicioActual?.origen as { id?: string } | undefined)?.id ??
          '';
        const destinoIdActual =
          (servicioActual?.destinoId as string | undefined) ??
          (servicioActual?.destino as { id?: string } | undefined)?.id ??
          '';
        if (state.origenId && state.origenId !== origenIdActual) cambios.origenId = state.origenId;
        if (state.destinoId && state.destinoId !== destinoIdActual)
          cambios.destinoId = state.destinoId;
        const natTrim = state.naturaleza.trim();
        if (natTrim && natTrim !== ((servicioActual?.naturaleza as string | undefined) ?? ''))
          cambios.naturaleza = natTrim;
        if (
          state.cantidad !== '' &&
          Number(state.cantidad) !== ((servicioActual?.cantidad as number | undefined) ?? NaN)
        )
          cambios.cantidad = Number(state.cantidad);
        if (state.unidadCantidad !== (servicioActual?.unidadCantidad ?? 'KG')) {
          cambios.unidadCantidad = state.unidadCantidad;
        }
        if (
          state.bultos !== '' &&
          Number(state.bultos) !== ((servicioActual?.bultos as number | undefined) ?? NaN)
        )
          cambios.bultos = Number(state.bultos);
        const obsTrim = state.observaciones.trim();
        if (obsTrim !== ((servicioActual?.observaciones as string | undefined) ?? '').trim()) {
          cambios.observaciones = obsTrim || undefined;
        }
        const deca = await modificar.mutateAsync({
          decaId: editingDeCA.id,
          input: {
            motivo: state.motivo.trim(),
            ...envioPatch,
            cambios: Object.keys(cambios).length > 0 ? cambios : undefined,
          },
        });
        notifications.show({
          title: 'DeCA modificado',
          message: `Se ha generado una nueva version (${deca.codigoAnual ?? deca.codigoPublico.slice(0, 12)}…).`,
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        navigate(`/deca/${deca.id}`, { replace: true });
        return;
      }
      // ── CREACION ──
      const deca = await crear.mutateAsync({
        tipoServicio: state.tipoServicio,
        cargadorId: state.cargadorId,
        transportistaId: state.transportistaId,
        vehiculoId: state.vehiculoId,
        conductorId: state.conductorId,
        fechaServicio: state.fechaCarga.toISOString().slice(0, 10),
        fechaFinServicio: state.fechaDescarga
          ? state.fechaDescarga.toISOString().slice(0, 10)
          : undefined,
        servicios: [
          {
            origenId: state.origenId,
            destinoId: state.destinoId,
            naturaleza: state.naturaleza.trim(),
            cantidad: Number(state.cantidad),
            unidadCantidad: state.unidadCantidad,
            bultos: Number(state.bultos),
            observaciones: state.observaciones.trim() || undefined,
          },
        ],
      });
      notifications.show({
        title: 'DeCA emitido',
        message: `Codigo: ${deca.codigoAnual ?? deca.codigoPublico.slice(0, 12)}…`,
        color: 'green',
        icon: <IconCheck size={18} />,
      });
      navigate(`/deca/${deca.id}`, { replace: true });
    } catch (e) {
      notifications.show({
        title: isEditing ? 'Error al modificar DeCA' : 'Error al emitir DeCA',
        message: getErrorMessage(e),
        color: 'red',
        icon: <IconAlertCircle size={18} />,
      });
    } finally {
      submittingRef.current = false;
    }
  };

  // ──────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────

  return (
    <Stack gap="md" maw={1100} mx="auto">
      {/* Cabecera */}
      <Group justify="space-between" align="center" px="md">
        <Group gap="sm">
          <ActionIcon variant="subtle" onClick={() => navigate(-1)} aria-label="Volver">
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Title order={2} fw={600}>
            {isEditing ? 'Modificar DeCA' : 'Nuevo DeCA'}
          </Title>
          {isEditing && editingDeCA && (
            <Badge variant="light" color="deca">
              {editingDeCA.codigoAnual ?? editingDeCA.codigoPublico.slice(0, 12)}
            </Badge>
          )}
        </Group>
        <Button
          variant="subtle"
          color="red"
          leftSection={<IconX size={16} />}
          onClick={() => navigate(isEditing && editingDeCA ? `/deca/${editingDeCA.id}` : '/deca')}
        >
          Cancelar
        </Button>
      </Group>

      <Card withBorder mx="md">
        <Stepper active={step} onStepClick={setStep} allowNextStepsSelect={false} color="deca">
          {STEP_LABELS.map((label) => (
            <Stepper.Step key={label} label={label} />
          ))}
        </Stepper>
      </Card>

      {/* Paso 0 — Partes */}
      {step === 0 && (
        <Card withBorder mx="md">
          <Stack gap="md">
            <Group gap="sm">
              <IconBuilding size={20} color="var(--mantine-color-deca-7)" />
              <Title order={4}>Partes del servicio</Title>
            </Group>
            <Text size="sm" c="dimmed">
              Selecciona las entidades que intervienen en el transporte. Si faltan en el catalogo,
              usa el &ldquo;+&rdquo; para crearlas in-place.
            </Text>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <CatalogField
                  label="Cargador contractual"
                  required
                  icon={IconBuilding}
                  value={state.cargadorId}
                  data={cargadores.map((e) => ({
                    value: e.id,
                    label: e.razonSocial,
                  }))}
                  rightSectionLabel={cargadores.length === 0 ? 'No hay cargadores' : undefined}
                  placeholder="Selecciona el cargador"
                  onChange={(v) => update('cargadorId', v ?? '')}
                  onCreate={() => setModalCatalogo({ tipo: 'crearEmpresa', subtipo: 'CARGADOR' })}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <CatalogField
                  label="Transportista efectivo"
                  required
                  icon={IconTruck}
                  value={state.transportistaId}
                  data={transportistas.map((e) => ({
                    value: e.id,
                    label: e.razonSocial,
                  }))}
                  rightSectionLabel={
                    transportistas.length === 0 ? 'No hay transportistas' : undefined
                  }
                  placeholder="Selecciona el transportista"
                  onChange={(v) => update('transportistaId', v ?? '')}
                  onCreate={() =>
                    setModalCatalogo({
                      tipo: 'crearEmpresa',
                      subtipo: 'TRANSPORTISTA',
                    })
                  }
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <CatalogField
                  label="Conductor"
                  required
                  icon={IconUser}
                  value={state.conductorId}
                  data={conductores.map((c) => ({
                    value: c.id,
                    label: c.nombre + (c.dni ? ` · ${c.dni}` : ''),
                  }))}
                  rightSectionLabel={conductores.length === 0 ? 'No hay conductores' : undefined}
                  placeholder="Selecciona el conductor"
                  onChange={(v) => update('conductorId', v ?? '')}
                  onCreate={() => setModalCatalogo({ tipo: 'crearConductor' })}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <CatalogField
                  label="Vehículo"
                  required
                  icon={IconTruck}
                  value={state.vehiculoId}
                  data={vehiculos.map((v) => ({
                    value: v.id,
                    label: v.matricula + (v.marca ? ` · ${v.marca} ${v.modelo ?? ''}` : ''),
                  }))}
                  rightSectionLabel={vehiculos.length === 0 ? 'No hay vehículos' : undefined}
                  placeholder="Selecciona el vehículo"
                  onChange={(v) => update('vehiculoId', v ?? '')}
                  onCreate={() => setModalCatalogo({ tipo: 'crearVehiculo' })}
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>
      )}

      {/* Paso 1 — Trayecto */}
      {step === 1 && (
        <Card withBorder mx="md">
          <Stack gap="md">
            <Group gap="sm">
              <IconRoute size={20} color="var(--mantine-color-deca-7)" />
              <Title order={4}>Trayecto</Title>
            </Group>
            <Text size="sm" c="dimmed">
              Origen y destino del servicio. Ambos deben ser puntos diferentes del catálogo del
              tenant.
            </Text>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <CatalogField
                  label="Origen"
                  required
                  icon={IconRoute}
                  value={state.origenId}
                  data={origenesDestinos.map((o) => ({
                    value: o.id,
                    label: o.nombre + (o.poblacion ? ` · ${o.poblacion}` : ''),
                  }))}
                  rightSectionLabel={origenesDestinos.length === 0 ? 'No hay orígenes' : undefined}
                  placeholder="Selecciona el origen"
                  onChange={(v) => update('origenId', v ?? '')}
                  onCreate={() => setModalCatalogo({ tipo: 'crearOrigenDestino' })}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <CatalogField
                  label="Destino"
                  required
                  icon={IconRoute}
                  value={state.destinoId}
                  data={origenesDestinos
                    .filter((o) => o.id !== state.origenId)
                    .map((o) => ({
                      value: o.id,
                      label: o.nombre + (o.poblacion ? ` · ${o.poblacion}` : ''),
                    }))}
                  placeholder="Selecciona el destino"
                  onChange={(v) => update('destinoId', v ?? '')}
                  onCreate={() => setModalCatalogo({ tipo: 'crearOrigenDestino' })}
                />
              </Grid.Col>
            </Grid>
            {state.origenId === state.destinoId && state.origenId && (
              <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
                Origen y destino deben ser puntos diferentes.
              </Alert>
            )}
          </Stack>
        </Card>
      )}

      {/* Paso 2 — Mercancía */}
      {step === 2 && (
        <Card withBorder mx="md">
          <Stack gap="md">
            <Group gap="sm">
              <IconCalendar size={20} color="var(--mantine-color-deca-7)" />
              <Title order={4}>Mercancía y fechas</Title>
            </Group>
            <Text size="sm" c="dimmed">
              Datos del material a transportar y de las fechas previstas del servicio.
            </Text>
            <Grid>
              <Grid.Col span={12}>
                <TextInput
                  label="Naturaleza de la mercancía"
                  required
                  placeholder="Ej: Mercancía general paletizada"
                  maxLength={200}
                  value={state.naturaleza}
                  onChange={(e) => update('naturaleza', e.currentTarget.value)}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <NumberInput
                  label="Cantidad"
                  required
                  min={0}
                  max={50000}
                  decimalScale={3}
                  value={state.cantidad}
                  onChange={(v) => update('cantidad', v === '' ? '' : Number(v))}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 2 }}>
                <Select
                  label="Unidad"
                  required
                  data={(['KG', 'T', 'M3', 'L', 'PALET'] as UnidadCantidad[]).map((u) => ({
                    value: u,
                    label: `${UNIDAD_CANTIDAD_LABEL[u]} (${UNIDAD_CANTIDAD_MEANING[u]})`,
                  }))}
                  value={state.unidadCantidad}
                  onChange={(v) => v && update('unidadCantidad', v as UnidadCantidad)}
                  allowDeselect={false}
                  comboboxProps={{ withinPortal: true }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <NumberInput
                  label="Bultos"
                  required
                  min={0}
                  max={100000}
                  value={state.bultos}
                  onChange={(v) => update('bultos', v === '' ? '' : Number(v))}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <DateInput
                  label="Fecha de carga"
                  required
                  value={state.fechaCarga}
                  onChange={(v) => update('fechaCarga', v)}
                  valueFormat="YYYY-MM-DD"
                  leftSection={<IconCalendar size={16} />}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <DateInput
                  label="Fecha de descarga (opcional)"
                  value={state.fechaDescarga}
                  onChange={(v) => update('fechaDescarga', v)}
                  valueFormat="YYYY-MM-DD"
                  leftSection={<IconCalendar size={16} />}
                  clearable
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <Textarea
                  label="Observaciones"
                  placeholder="Notas operativas: manejo especial, instrucciones del cliente…"
                  minRows={2}
                  maxLength={500}
                  value={state.observaciones}
                  onChange={(e) => update('observaciones', e.currentTarget.value)}
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>
      )}

      {/* Paso 3 — Confirmar */}
      {step === 3 && (
        <Card withBorder mx="md">
          <Stack gap="md">
            <Group gap="sm">
              <IconCircleCheck size={20} color="var(--mantine-color-deca-7)" />
              <Title order={4}>Resumen</Title>
            </Group>
            <Divider />
            <SummaryRow
              icon={IconBuilding}
              label="Cargador contractual"
              value={labelFor(cargadores, state.cargadorId, (e) => e.razonSocial)}
            />
            <SummaryRow
              icon={IconTruck}
              label="Transportista efectivo"
              value={labelFor(transportistas, state.transportistaId, (e) => e.razonSocial)}
            />
            <SummaryRow
              icon={IconUser}
              label="Conductor"
              value={labelFor(conductores, state.conductorId, (c) => c.nombre)}
            />
            <SummaryRow
              icon={IconTruck}
              label="Vehículo"
              value={labelFor(vehiculos, state.vehiculoId, (v) => v.matricula)}
            />
            <Divider />
            <SummaryRow
              icon={IconRoute}
              label="Origen"
              value={labelFor(origenesDestinos, state.origenId, (o) => o.nombre)}
            />
            <SummaryRow
              icon={IconRoute}
              label="Destino"
              value={labelFor(origenesDestinos, state.destinoId, (o) => o.nombre)}
            />
            <Divider />
            <SummaryRow
              icon={IconArrowBigUp}
              label="Fecha de carga"
              value={state.fechaCarga?.toLocaleDateString('es-ES') ?? '—'}
            />
            <SummaryRow
              icon={IconArrowBigDown}
              label="Fecha de descarga"
              value={state.fechaDescarga?.toLocaleDateString('es-ES') ?? '—'}
            />
            <SummaryRow icon={IconPencil} label="Naturaleza" value={state.naturaleza} />
            <SummaryRow
              icon={IconTruck}
              label="Cantidad"
              value={
                state.cantidad !== ''
                  ? `${state.cantidad} ${UNIDAD_CANTIDAD_LABEL[state.unidadCantidad]}`
                  : '—'
              }
            />
            <SummaryRow icon={IconTruck} label="Bultos" value={String(state.bultos)} />
            {state.observaciones.trim() && (
              <SummaryRow icon={IconPencil} label="Observaciones" value={state.observaciones} />
            )}
            <Divider />
            {isEditing && (
              <Textarea
                label="Motivo de la modificación"
                description="Obligatorio por Apartado Quinto de la Resolución 5 de junio de 2026."
                placeholder="Ej: Cambio de vehículo por avería y corrección de peso en báscula"
                minRows={2}
                maxLength={500}
                required
                value={state.motivo}
                onChange={(e) => update('motivo', e.currentTarget.value)}
              />
            )}
            {!isEditing && (
              <Alert color="deca" variant="light" icon={<IconCheck size={16} />}>
                Al pulsar &ldquo;Emitir DeCA&rdquo; se creará el envío en estado EMITIDO y se
                generará el documento con su código público.
              </Alert>
            )}
            <Text size="xs" c="dimmed">
              Revisa los datos antes de {isEditing ? 'guardar' : 'emitir'}: la exactitud del
              documento es responsabilidad de quien lo emite.
            </Text>
          </Stack>
        </Card>
      )}

      {/* Botones de navegación */}
      <Group justify="space-between" px="md" pb="md">
        <Button
          variant="default"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
        >
          Atrás
        </Button>
        {step < STEP_LABELS.length - 1 ? (
          <Button
            rightSection={<IconArrowRight size={16} />}
            onClick={() => setStep((s) => s + 1)}
            disabled={
              (step === 0 && !canNextFrom0) ||
              (step === 1 && !canNextFrom1) ||
              (step === 2 && !canNextFrom2)
            }
          >
            Siguiente
          </Button>
        ) : (
          <Button
            leftSection={<IconCheck size={16} />}
            color="deca"
            onClick={handleSubmit}
            disabled={!canSubmit || crear.isPending || modificar.isPending}
            loading={crear.isPending || modificar.isPending}
          >
            {isEditing ? 'Guardar cambios' : 'Emitir DeCA'}
          </Button>
        )}
      </Group>

      {/* Modal-catalogo: alta in-place de empresas/vehículos/conductores/origenes */}
      {modalCatalogo && (
        <CatalogoModal
          modal={modalCatalogo}
          onClose={() => setModalCatalogo(null)}
          onCreated={(id) => {
            // Asignamos el ID al campo que origino la accion.
            if (modalCatalogo.tipo === 'crearEmpresa') {
              if (modalCatalogo.subtipo === 'CARGADOR') update('cargadorId', id);
              else update('transportistaId', id);
            } else if (modalCatalogo.tipo === 'crearVehiculo') {
              update('vehiculoId', id);
            } else if (modalCatalogo.tipo === 'crearConductor') {
              update('conductorId', id);
            } else if (modalCatalogo.tipo === 'crearOrigenDestino') {
              // Si el campo origen esta vacio, lo volcamos ahi; si no, a destino.
              if (!state.origenId) update('origenId', id);
              else update('destinoId', id);
            }
            setModalCatalogo(null);
          }}
        />
      )}
    </Stack>
  );
}

// ────────────────────────────────────────────────────────────────────
// Subcomponentes
// ────────────────────────────────────────────────────────────────────

interface CatalogFieldProps {
  label: string;
  required?: boolean;
  icon: typeof IconBuilding;
  data: { value: string; label: string }[];
  value: string;
  placeholder: string;
  rightSectionLabel?: string;
  onChange: (v: string | null) => void;
  onCreate: () => void;
}

/**
 * Select de catalogo con boton "+" integrado a la derecha. Si la lista
 * esta vacia, pintamos un aviso en vez del Select vacio (UX mas clara).
 */
function CatalogField({
  label,
  required,
  icon: IconCmp,
  data,
  value,
  placeholder,
  rightSectionLabel,
  onChange,
  onCreate,
}: CatalogFieldProps) {
  if (data.length === 0) {
    return (
      <Stack gap={4}>
        <Text size="sm" fw={500}>
          {label} {required && <span style={{ color: 'red' }}>*</span>}
        </Text>
        <Group gap="xs" align="center" wrap="nowrap">
          <IconCmp size={16} color="var(--mantine-color-gray-6)" />
          <Text size="sm" c="dimmed">
            {rightSectionLabel ?? 'Sin candidatos.'}
          </Text>
          <Button size="xs" variant="light" onClick={onCreate} leftSection={<IconPlus size={14} />}>
            Crear
          </Button>
        </Group>
      </Stack>
    );
  }
  return (
    <Select
      label={
        <Group gap={6}>
          <IconCmp size={14} />
          <Text size="sm" fw={500}>
            {label} {required && <span style={{ color: 'red' }}>*</span>}
          </Text>
        </Group>
      }
      data={data}
      value={value || null}
      placeholder={placeholder}
      searchable
      clearable
      onChange={onChange}
      rightSection={
        <ActionIcon variant="subtle" onClick={onCreate} aria-label={`Crear ${label.toLowerCase()}`}>
          <IconPlus size={16} />
        </ActionIcon>
      }
    />
  );
}

interface SummaryRowProps {
  icon: typeof IconBuilding;
  label: string;
  value: string;
}
function SummaryRow({ icon: IconCmp, label, value }: SummaryRowProps) {
  return (
    <Group justify="space-between" align="center">
      <Group gap="xs">
        <IconCmp size={16} color="var(--mantine-color-gray-7)" />
        <Text size="sm" c="dimmed">
          {label}
        </Text>
      </Group>
      <Text fw={500}>{value || '—'}</Text>
    </Group>
  );
}

// ────────────────────────────────────────────────────────────────────
// CatalogoModal: alta in-place de empresa/vehículo/conductor/origen-destino
// ────────────────────────────────────────────────────────────────────

interface CatalogoModalProps {
  modal: NonNullable<ModalCatalogo>;
  onClose: () => void;
  onCreated: (id: string) => void;
}

function CatalogoModal({ modal, onClose, onCreated }: CatalogoModalProps) {
  const title = (() => {
    switch (modal.tipo) {
      case 'crearEmpresa':
        return modal.subtipo === 'CARGADOR'
          ? 'Nuevo cargador contractual'
          : 'Nuevo transportista efectivo';
      case 'crearVehiculo':
        return 'Nuevo vehículo';
      case 'crearConductor':
        return 'Nuevo conductor';
      case 'crearOrigenDestino':
        return 'Nuevo origen / destino';
    }
  })();

  return (
    <Modal opened onClose={onClose} title={title} size="lg" centered>
      {modal.tipo === 'crearEmpresa' && (
        <EmpresaForm subtipo={modal.subtipo} onCreated={onCreated} onClose={onClose} />
      )}
      {modal.tipo === 'crearVehiculo' && <VehiculoForm onCreated={onCreated} onClose={onClose} />}
      {modal.tipo === 'crearConductor' && <ConductorForm onCreated={onCreated} onClose={onClose} />}
      {modal.tipo === 'crearOrigenDestino' && (
        <OrigenDestinoForm onCreated={onCreated} onClose={onClose} />
      )}
    </Modal>
  );
}

function EmpresaForm({
  subtipo,
  onCreated,
  onClose,
}: {
  subtipo: 'CARGADOR' | 'TRANSPORTISTA';
  onCreated: (id: string) => void;
  onClose: () => void;
}) {
  const crear = useCreateEmpresa();
  const [values, setValues] = useState<EmpresaDraft>({
    razonSocial: '',
    nifCif: '',
    domicilioFiscal: '',
    municipio: '',
    codigoPostal: '',
    provincia: '',
    telefono: '',
    email: '',
  });
  const canSubmit =
    !!values.razonSocial.trim() && !!values.nifCif.trim() && !!values.domicilioFiscal.trim();
  return (
    <Stack gap="sm">
      <Grid>
        <Grid.Col span={12}>
          <TextInput
            label="Razón social"
            required
            value={values.razonSocial}
            onChange={(e) => setValues({ ...values, razonSocial: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="NIF / CIF"
            required
            value={values.nifCif}
            onChange={(e) => setValues({ ...values, nifCif: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Teléfono"
            value={values.telefono}
            onChange={(e) => setValues({ ...values, telefono: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label="Domicilio fiscal"
            required
            value={values.domicilioFiscal}
            onChange={(e) => setValues({ ...values, domicilioFiscal: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Municipio"
            value={values.municipio}
            onChange={(e) => setValues({ ...values, municipio: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Código postal"
            value={values.codigoPostal}
            onChange={(e) => setValues({ ...values, codigoPostal: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Provincia"
            value={values.provincia}
            onChange={(e) => setValues({ ...values, provincia: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Email"
            type="email"
            value={values.email}
            onChange={(e) => setValues({ ...values, email: e.currentTarget.value })}
          />
        </Grid.Col>
      </Grid>
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose} disabled={crear.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={async () => {
            try {
              const r = await crear.mutateAsync({
                tipo: subtipo,
                razonSocial: values.razonSocial.trim(),
                nifCif: values.nifCif.trim(),
                domicilioFiscal: values.domicilioFiscal.trim(),
                municipio: values.municipio.trim() || undefined,
                codigoPostal: values.codigoPostal.trim() || undefined,
                provincia: values.provincia.trim() || undefined,
                telefono: values.telefono.trim() || undefined,
                email: values.email.trim() || undefined,
              });
              notifications.show({
                message: 'Empresa creada',
                color: 'green',
                icon: <IconCheck size={16} />,
              });
              onCreated(r.id);
            } catch (e) {
              notifications.show({
                message: getErrorMessage(e),
                color: 'red',
                icon: <IconAlertCircle size={16} />,
              });
            }
          }}
          disabled={!canSubmit}
          loading={crear.isPending}
        >
          Crear
        </Button>
      </Group>
    </Stack>
  );
}

function VehiculoForm({
  onCreated,
  onClose,
}: {
  onCreated: (id: string) => void;
  onClose: () => void;
}) {
  const crear = useCreateVehiculo();
  const [values, setValues] = useState<VehiculoDraft>({
    matricula: '',
    matriculaRemolque: '',
    marca: '',
    modelo: '',
    tara: '',
    mma: '',
  });
  const canSubmit = !!values.matricula.trim();
  return (
    <Stack gap="sm">
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Matrícula"
            required
            placeholder="1234-ABC"
            value={values.matricula}
            onChange={(e) =>
              setValues({ ...values, matricula: e.currentTarget.value.toUpperCase() })
            }
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Matrícula remolque"
            value={values.matriculaRemolque}
            onChange={(e) =>
              setValues({
                ...values,
                matriculaRemolque: e.currentTarget.value.toUpperCase(),
              })
            }
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Marca"
            value={values.marca}
            onChange={(e) => setValues({ ...values, marca: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Modelo"
            value={values.modelo}
            onChange={(e) => setValues({ ...values, modelo: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <NumberInput
            label="Tara (kg)"
            min={0}
            value={values.tara}
            onChange={(v) => setValues({ ...values, tara: v === '' ? '' : Number(v) })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <NumberInput
            label="MMA (kg)"
            min={0}
            value={values.mma}
            onChange={(v) => setValues({ ...values, mma: v === '' ? '' : Number(v) })}
          />
        </Grid.Col>
      </Grid>
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose} disabled={crear.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={async () => {
            try {
              const r = await crear.mutateAsync({
                matricula: values.matricula.trim(),
                matriculaRemolque: values.matriculaRemolque.trim() || undefined,
                marca: values.marca.trim() || undefined,
                modelo: values.modelo.trim() || undefined,
                tara: values.tara === '' ? undefined : Number(values.tara),
                mma: values.mma === '' ? undefined : Number(values.mma),
              });
              notifications.show({
                message: 'Vehículo creado',
                color: 'green',
                icon: <IconCheck size={16} />,
              });
              onCreated(r.id);
            } catch (e) {
              notifications.show({
                message: getErrorMessage(e),
                color: 'red',
                icon: <IconAlertCircle size={16} />,
              });
            }
          }}
          disabled={!canSubmit}
          loading={crear.isPending}
        >
          Crear
        </Button>
      </Group>
    </Stack>
  );
}

function ConductorForm({
  onCreated,
  onClose,
}: {
  onCreated: (id: string) => void;
  onClose: () => void;
}) {
  const crear = useCreateConductor();
  const [values, setValues] = useState<ConductorDraft>({
    nombre: '',
    dni: '',
    telefono: '',
    email: '',
    direccion: '',
  });
  const canSubmit = !!values.nombre.trim();
  return (
    <Stack gap="sm">
      <Grid>
        <Grid.Col span={12}>
          <TextInput
            label="Nombre completo"
            required
            value={values.nombre}
            onChange={(e) => setValues({ ...values, nombre: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="DNI / NIE"
            value={values.dni}
            onChange={(e) => setValues({ ...values, dni: e.currentTarget.value.toUpperCase() })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label="Teléfono"
            value={values.telefono}
            onChange={(e) => setValues({ ...values, telefono: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label="Email"
            type="email"
            value={values.email}
            onChange={(e) => setValues({ ...values, email: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label="Dirección"
            value={values.direccion}
            onChange={(e) => setValues({ ...values, direccion: e.currentTarget.value })}
          />
        </Grid.Col>
      </Grid>
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose} disabled={crear.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={async () => {
            try {
              const r = await crear.mutateAsync({
                nombre: values.nombre.trim(),
                dni: values.dni.trim() || undefined,
                telefono: values.telefono.trim() || undefined,
                email: values.email.trim() || undefined,
                direccion: values.direccion.trim() || undefined,
              });
              notifications.show({
                message: 'Conductor creado',
                color: 'green',
                icon: <IconCheck size={16} />,
              });
              onCreated(r.id);
            } catch (e) {
              notifications.show({
                message: getErrorMessage(e),
                color: 'red',
                icon: <IconAlertCircle size={16} />,
              });
            }
          }}
          disabled={!canSubmit}
          loading={crear.isPending}
        >
          Crear
        </Button>
      </Group>
    </Stack>
  );
}

function OrigenDestinoForm({
  onCreated,
  onClose,
}: {
  onCreated: (id: string) => void;
  onClose: () => void;
}) {
  const crear = useCreateOrigenDestino();
  const [values, setValues] = useState<OrigenDestinoDraft>({
    nombre: '',
    direccion: '',
    codigoPostal: '',
    poblacion: '',
    provincia: '',
    nifCif: '',
  });
  const canSubmit = !!values.nombre.trim();
  return (
    <Stack gap="sm">
      <Grid>
        <Grid.Col span={12}>
          <TextInput
            label="Nombre"
            required
            placeholder="Ej: Almacén central Madrid"
            value={values.nombre}
            onChange={(e) => setValues({ ...values, nombre: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label="Dirección"
            value={values.direccion}
            onChange={(e) => setValues({ ...values, direccion: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput
            label="Código postal"
            value={values.codigoPostal}
            onChange={(e) => setValues({ ...values, codigoPostal: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput
            label="Población"
            value={values.poblacion}
            onChange={(e) => setValues({ ...values, poblacion: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput
            label="Provincia"
            value={values.provincia}
            onChange={(e) => setValues({ ...values, provincia: e.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label="NIF / CIF del destinatario"
            value={values.nifCif}
            onChange={(e) => setValues({ ...values, nifCif: e.currentTarget.value.toUpperCase() })}
          />
        </Grid.Col>
      </Grid>
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose} disabled={crear.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={async () => {
            try {
              const r = await crear.mutateAsync({
                nombre: values.nombre.trim(),
                direccion: values.direccion.trim() || undefined,
                codigoPostal: values.codigoPostal.trim() || undefined,
                poblacion: values.poblacion.trim() || undefined,
                provincia: values.provincia.trim() || undefined,
                nifCif: values.nifCif.trim() || undefined,
              });
              notifications.show({
                message: 'Origen / destino creado',
                color: 'green',
                icon: <IconCheck size={16} />,
              });
              onCreated(r.id);
            } catch (e) {
              notifications.show({
                message: getErrorMessage(e),
                color: 'red',
                icon: <IconAlertCircle size={16} />,
              });
            }
          }}
          disabled={!canSubmit}
          loading={crear.isPending}
        >
          Crear
        </Button>
      </Group>
    </Stack>
  );
}

// ────────────────────────────────────────────────────────────────────
// Wrapper de rutas: detecta modo edicion vs creacion segun el path
// ────────────────────────────────────────────────────────────────────

/**
 * Wrapper para `/deca/nuevo` y `/deca/:id/editar`. Carga el DeCA con
 * `useDeCA(id)` y se lo pasa a `DeCAWizard` (mismo patron que el
 * wrapper `EditarDeCA` del movil). Asi reutilizamos el wizard como
 * editor completo.
 */
export function DeCAWizardRoute() {
  const { id } = useParams<{ id: string }>();
  const { data: deca, isLoading, error } = useDeCA(id ?? '');

  if (!id) {
    // Modo creacion
    return <DeCAWizard />;
  }

  if (isLoading || !deca) {
    return (
      <Center mih="60vh">
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Cargando DeCA…
          </Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Stack p="md">
        <Alert
          icon={<IconAlertCircle size={18} />}
          color="red"
          variant="light"
          title="No hemos podido cargar el DeCA"
        >
          {getErrorMessage(error)}
        </Alert>
      </Stack>
    );
  }

  return <DeCAWizard editingDeCA={deca} />;
}
